import type { AuditRecord, ChangeRequest, OpsData, PortalUser } from '@urbanrats/ops-core';
import type { DevView, OpsBackend, PipelineView, ReqOutcome } from './types.ts';

/**
 * Real backend: the server authenticates (SSO adapter + TOTP), authorises every call (RBAC), scopes
 * data by tenant, and runs maker-checker and the hash-chained audit in PostgreSQL. The admin token is
 * kept for this tab only.
 */
const API = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:8787';
const KEY = 'urbanrats.admin.token.v1';
const errText = (b: unknown) => {
  const e = b as { code?: string; message?: string } | null;
  return `${e?.code ?? 'ERROR'}: ${e?.message ?? 'request failed'}`;
};

export async function createBackend(): Promise<OpsBackend> {
  let token = sessionStorage.getItem(KEY);
  let me: PortalUser | null = null;
  let snapshot: OpsData | null = null;
  let reqs: ChangeRequest[] = [];
  let audit: { records: AuditRecord[]; chainIntact: boolean } = { records: [], chainIntact: true };

  const call = async <T>(method: string, path: string, body?: unknown): Promise<{ status: number; body: T }> => {
    const r = await fetch(`${API}${path}`, { method, headers: { ...(token ? { authorization: `Bearer ${token}` } : {}), ...(body !== undefined ? { 'content-type': 'application/json' } : {}) }, body: body !== undefined ? JSON.stringify(body) : undefined });
    const text = await r.text();
    // Session over (8 h, signed out elsewhere, account disabled): drop it and send the user back to sign-in
    // instead of leaving every page frozen on stale data.
    if (r.status === 401 && token && !path.startsWith('/v1/admin/auth/')) {
      token = null;
      me = null;
      sessionStorage.removeItem(KEY);
      window.dispatchEvent(new CustomEvent('urbanrats:admin-session-ended'));
    }
    return { status: r.status, body: (text ? JSON.parse(text) : null) as T };
  };
  const refresh = async () => {
    if (!token) return;
    const m = await call<PortalUser>('GET', '/v1/admin/me');
    if (m.status !== 200) {
      token = null;
      me = null;
      sessionStorage.removeItem(KEY);
      return;
    }
    me = m.body;
    const [d, c, a] = await Promise.all([
      call<OpsData>('GET', '/v1/admin/ops-data'),
      call<{ requests: ChangeRequest[] }>('GET', '/v1/admin/change-requests'),
      call<{ chainIntact: boolean; records: { seq: string; at: string; actor: string; role: string; action: string; target: string; reason: string; request_id: string | null; prev_hash: string; hash: string }[] }>('GET', '/v1/admin/audit'),
    ]);
    if (d.status !== 200) throw new Error(`ops data: ${d.status}`);
    snapshot = d.body;
    reqs = c.status === 200 ? c.body.requests : [];
    audit =
      a.status === 200
        ? { chainIntact: a.body.chainIntact, records: a.body.records.map((r) => ({ seq: Number(r.seq), at: r.at, actor: r.actor, role: r.role, action: r.action, target: r.target, reason: r.reason, requestId: r.request_id, prevHash: r.prev_hash, hash: r.hash })) }
        : { chainIntact: true, records: [] };
  };
  await refresh();
  const asReq = (r: { status: number; body: ChangeRequest | { code: string; message: string } }): ReqOutcome =>
    r.status === 200 ? { ok: true, value: r.body as ChangeRequest } : { ok: false, error: r.body as { code: string; message: string } };

  return {
    kind: 'api',
    auth: {
      mode: 'login',
      current: () => me,
      password: async (userId, password) => {
        const r = await call<{ stage: 'ENROLL' | 'TOTP'; challenge: string; enrolment?: { secret: string; uri: string } } | { code: string; message: string }>('POST', '/v1/admin/auth/password', { userId, password });
        if (r.status === 200) return { ok: true, value: r.body as { stage: 'ENROLL' | 'TOTP'; challenge: string; enrolment?: { secret: string; uri: string } } };
        const e = r.body as { code?: string; message?: string } | null;
        return { ok: false, error: r.status === 423 || r.status === 429 ? (e?.message ?? 'try again later') : 'Sign-in failed (user, password or code)' };
      },
      code: async (challenge, code) => {
        const r = await call<{ token: string; suggestPasswordChange: boolean; passwordWeak: boolean; enrolled: boolean } | { code: string; message: string }>('POST', '/v1/admin/auth/totp', { challenge, code });
        if (r.status !== 200) return { ok: false, error: (r.body as { message?: string } | null)?.message ?? 'Sign-in failed' };
        const b = r.body as { token: string; suggestPasswordChange: boolean; passwordWeak: boolean; enrolled: boolean };
        token = b.token;
        sessionStorage.setItem(KEY, token);
        await refresh();
        return me ? { ok: true, value: { user: me, suggestPasswordChange: b.suggestPasswordChange, passwordWeak: b.passwordWeak, enrolled: b.enrolled } } : { ok: false, error: 'Sign-in failed' };
      },
      changePassword: async (currentPassword, newPassword) => {
        const r = await call<{ ok: true } | { code: string; message: string }>('POST', '/v1/admin/account/password', { currentPassword, newPassword });
        return r.status === 200 ? { ok: true, value: 'Password changed' } : { ok: false, error: (r.body as { message?: string } | null)?.message ?? 'Could not change the password' };
      },
    },
    data: () => {
      if (!snapshot) throw new Error('not signed in');
      return snapshot;
    },
    refresh,
    requests: () => reqs,
    audit: () => ({ ...audit, events: [], serverSide: true }),
    setGamePaused: async (_u, gameId, paused, reason) => {
      const r = await call<{ message: string } | { code: string; message: string }>('POST', `/v1/admin/games/${encodeURIComponent(gameId)}/pause`, { paused, reason });
      await refresh();
      return r.status === 200 ? { ok: true, value: (r.body as { message: string }).message } : { ok: false, error: `${(r.body as { code: string }).code}: ${(r.body as { message: string }).message}` };
    },
    request: async (_u, kind, target, summary, payload, reason) => {
      const r = asReq(await call('POST', '/v1/admin/change-requests', { kind, target, summary, payload, reason }));
      await refresh();
      return r;
    },
    decide: async (_u, id, approve, reason) => {
      const r = asReq(await call('POST', `/v1/admin/change-requests/${encodeURIComponent(id)}/decision`, { approve, reason }));
      await refresh();
      return r;
    },
    lightningOn: () => snapshot?.lightningPolicy !== null && snapshot?.lightningPolicy !== undefined,
    server: {
      apiBase: API,
      get: async <T,>(path: string) => {
        const r = await call<T | { code: string; message: string }>('GET', path);
        return r.status === 200 ? { ok: true as const, value: r.body as T } : { ok: false as const, error: errText(r.body) };
      },
      post: async <T,>(path: string, body: Record<string, unknown>) => {
        const r = await call<T | { code: string; message: string }>('POST', path, body);
        return r.status === 200 ? { ok: true as const, value: r.body as T } : { ok: false as const, error: errText(r.body) };
      },
      del: async <T,>(path: string, body: Record<string, unknown>) => {
        const r = await call<T | { code: string; message: string }>('DELETE', path, body);
        return r.status === 200 ? { ok: true as const, value: r.body as T } : { ok: false as const, error: errText(r.body) };
      },
      blob: async (path: string) => {
        const r = await fetch(`${API}${path}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
        if (r.status !== 200) return { ok: false as const, error: errText(await r.json().catch(() => null)) };
        return { ok: true as const, value: { url: URL.createObjectURL(await r.blob()), source: r.headers.get('x-preview-source'), capturedAt: r.headers.get('x-captured-at') } };
      },
      download: async (path, filename) => {
        const r = await fetch(`${API}${path}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
        if (r.status !== 200) return { ok: false, error: errText(await r.json().catch(() => null)) };
        const a = document.createElement('a');
        a.href = URL.createObjectURL(await r.blob());
        a.download = filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        return { ok: true, value: `${filename} downloaded (export audited on the server)` };
      },
    },
    cameraOps: {
      get: async () => {
        const r = await call<PipelineView | { code: string; message: string }>('GET', '/v1/admin/cameras/pipeline');
        return r.status === 200 ? { ok: true, value: r.body as PipelineView } : { ok: false, error: errText(r.body) };
      },
      action: async (cameraId, action, reason) => {
        const r = await call<{ from: string } | { code: string; message: string }>('POST', `/v1/admin/cameras/${encodeURIComponent(cameraId)}/state`, { action, reason });
        return r.status === 200 ? { ok: true, value: `${action} applied (was ${(r.body as { from: string }).from})` } : { ok: false, error: errText(r.body) };
      },
      check: async (cameraId) => {
        const r = await call<{ ok: boolean; failedStage?: string; error?: string } | { code: string; message: string }>('POST', `/v1/admin/cameras/${encodeURIComponent(cameraId)}/check`, {});
        if (r.status !== 200) return { ok: false, error: errText(r.body) };
        const c = r.body as { ok: boolean; failedStage?: string; error?: string };
        return { ok: true, value: c.ok ? 'health check passed' : `health check failed at ${c.failedStage}: ${c.error}` };
      },
      preview: async (assetId) => {
        const r = await call<{ playlistUrl: string } | { code: string; message: string }>('POST', `/v1/admin/round-media/${encodeURIComponent(assetId)}/preview`, {});
        return r.status === 200 ? { ok: true, value: `${API}${(r.body as { playlistUrl: string }).playlistUrl}` } : { ok: false, error: errText(r.body) };
      },
    },
    developer: {
      apiBase: API,
      get: async (operatorId) => {
        const r = await call<DevView | { code: string; message: string }>('GET', `/v1/admin/developer${operatorId ? `?operatorId=${encodeURIComponent(operatorId)}` : ''}`);
        return r.status === 200 ? { ok: true, value: r.body as DevView } : { ok: false, error: `${(r.body as { code: string }).code}: ${(r.body as { message: string }).message}` };
      },
      post: async <T,>(path: string, body: Record<string, unknown>) => {
        const r = await call<T | { code: string; message: string }>('POST', path, body);
        return r.status === 200 ? { ok: true as const, value: r.body as T } : { ok: false as const, error: `${(r.body as { code: string }).code}: ${(r.body as { message: string }).message}` };
      },
    },
  };
}
